import {
  Button,
  DetailList,
  formatDateTime,
  formatMoney,
  PageEmpty,
  PageError,
  PageHeader,
  PageLoading,
  TextButton,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { Textarea } from "@majitap/ui/arc/textarea/textarea";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import { useRecordCrumb } from "../../crumb";
import { messageFor } from "../../error-copy";
import { openFile } from "../../files";
import {
  amountText,
  BASIS,
  historyOf,
  pricingActions,
  RULE_STATE,
  type Rule,
  ruleActionsFor,
  ruleQuery,
  SCOPE_KEYS,
  scopeText,
} from "../../pricing";
import { meQuery } from "../../session";
import { ConfirmDialog } from "../shared/confirm";
import { ActivityPanel } from "../shared/panels";
import styles from "./pricing.module.css";
import { amountOk, MomentField, momentOf } from "./rule-form";

/** The rules with this rule's charge type and scope: its versions (`historyOf` keeps the exact scope). */
const versionsQuery = (rule: Rule) => ({
  queryKey: ["pricing", "versions", rule.charge_type.id, rule.scope],
  queryFn: async () => {
    const scope = Object.fromEntries(SCOPE_KEYS.filter((k) => rule.scope[k]).map((k) => [k, rule.scope[k] as string]));
    const page = await ok(
      api.GET("/v1/admin/pricing", {
        params: { query: { charge_type_id: rule.charge_type.id, ...scope, limit: 100 } },
      }),
    );
    return historyOf(rule, page.data);
  },
});

/** One pricing rule (P8-19): what it charges, to whom, when, and its versions. Prices change by superseding. */
export function RulePage(): ReactNode {
  const { ruleId } = useParams({ from: "/console/pricing/$ruleId" });
  const { data: me } = useSuspenseQuery(meQuery);
  const rule = useQuery(ruleQuery(ruleId));
  useRecordCrumb(rule.data?.charge_type.name);
  if (rule.status === "pending") return <PageLoading label="Loading the rule" />;
  if (rule.status === "error") {
    const error = errorOf(rule.error);
    if (error.code === "PRICING_NOT_FOUND")
      return <PageEmpty title="No such rule" description="It may have been deleted before it took effect." />;
    return <PageError message={messageFor(error)} onRetry={() => rule.refetch()} />;
  }
  return <RuleView rule={rule.data} canAudit={me?.permissions.includes("staff.manage") ?? false} />;
}

function RuleView({ rule: r, canAudit }: { rule: Rule; canAudit: boolean }) {
  const format = useCompanyFormat();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const versions = useQuery(versionsQuery(r));
  const can = ruleActionsFor(r.state);
  const [dialog, setDialog] = useState<"supersede" | "end" | "delete" | null>(null);
  const money = (v: string) => formatMoney(v, { ...format, currency: r.currency });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["pricing"] });
  const [label, tone] = RULE_STATE[r.state];
  return (
    <>
      <PageHeader
        title={`${r.charge_type.name}: ${amountText(r, money)}`}
        description={scopeText(r)}
        actions={
          <div className={styles.actions}>
            {can.remove ? (
              <Button variant="ghost" onClick={() => setDialog("delete")}>
                Delete
              </Button>
            ) : null}
            {can.end ? (
              <Button variant="secondary" onClick={() => setDialog("end")}>
                End
              </Button>
            ) : null}
            {can.supersede ? <Button onClick={() => setDialog("supersede")}>Change the price</Button> : null}
          </div>
        }
      />
      {r.scope.partner_id ? (
        <Alert tone="warning" title="This partner rate doesn't apply yet">
          MajiTap doesn't use a partner's own rate when it prices a delivery; the zone rate applies. Roadmap P8-35.
        </Alert>
      ) : null}
      <Alert tone="info" title="Prices aren't edited">
        Change the price supersedes this rule: it ends, and a new version with the new amount starts at the same moment,
        so what applied when stays on record.
      </Alert>
      <section className={styles.card}>
        <DetailList
          items={[
            { label: "State", value: <Badge tone={tone}>{label}</Badge> },
            {
              label: "Charge",
              value: `${r.charge_type.name} (${r.charge_type.direction === "payable" ? "a cost" : "charged to the customer"})`,
            },
            { label: "Amount", value: amountText(r, money) },
            { label: "Basis", value: BASIS[r.basis] },
            { label: "Applies to", value: scopeText(r) },
            { label: "From", value: formatDateTime(r.starts_at, format) },
            { label: "Until", value: r.ends_at ? formatDateTime(r.ends_at, format) : "No end" },
            { label: "Priority", value: String(r.priority) },
            { label: "Notes", value: r.notes },
            {
              label: "Contract",
              value: r.contract_file_id ? <ContractLink fileId={r.contract_file_id} /> : null,
            },
          ]}
        />
      </section>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Versions</h2>
        <SortableDataTable
          caption="Versions"
          rowKey="id"
          rows={versions.data ?? []}
          itemName={{ one: "version", other: "versions" }}
          emptyMessage={versions.isPending ? "Loading…" : "Only this one"}
          columns={[
            {
              key: "amount",
              label: "Amount",
              sortable: false,
              render: (_v, v) =>
                v.id === r.id ? (
                  <strong>{amountText(v, money)}</strong>
                ) : (
                  <Link to="/pricing/$ruleId" params={{ ruleId: v.id }} className={styles.link}>
                    {amountText(v, money)}
                  </Link>
                ),
            },
            {
              key: "starts_at",
              label: "From",
              sortable: false,
              render: (_v, v) => formatDateTime(v.starts_at, format),
            },
            {
              key: "ends_at",
              label: "Until",
              sortable: false,
              render: (_v, v) => (v.ends_at ? formatDateTime(v.ends_at, format) : "No end"),
            },
            {
              key: "state",
              label: "State",
              sortable: false,
              render: (_v, v) => <Badge tone={RULE_STATE[v.state][1]}>{RULE_STATE[v.state][0]}</Badge>,
            },
          ]}
        />
      </section>
      {canAudit ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Activity</h2>
          <ActivityPanel targetType="pricing" id={r.id} label="What happened to this rule" />
        </section>
      ) : null}
      {dialog === "supersede" ? (
        <SupersedeDialog
          rule={r}
          onDone={(next) => {
            setDialog(null);
            void navigate({ to: "/pricing/$ruleId", params: { ruleId: next } });
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === "end" ? <EndDialog rule={r} onClose={() => setDialog(null)} /> : null}
      {dialog === "delete" ? (
        <ConfirmDialog
          title="Delete this rule?"
          description="It hasn't taken effect yet, so nothing was ever priced with it."
          action="Delete"
          danger
          run={() => pricingActions.remove(r.id)}
          onDone={async () => {
            await refresh();
            await navigate({ to: "/pricing" });
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}

/** The new amount, from now or a day: this version ends then and the next starts. */
function SupersedeDialog({
  rule,
  onDone,
  onClose,
}: {
  rule: Rule;
  onDone: (nextId: string) => void;
  onClose: () => void;
}) {
  const format = useCompanyFormat();
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState(rule.amount);
  const [from, setFrom] = useState<Date | null>(null);
  const [notes, setNotes] = useState("");
  const save = useMutation({
    mutationFn: () =>
      pricingActions.supersede(rule.id, {
        amount: amount.trim(),
        starts_at: momentOf(from, format.timezone),
        notes: notes.trim() || undefined,
      }),
    onSuccess: async ({ started }) => {
      await queryClient.invalidateQueries({ queryKey: ["pricing"] });
      onDone(started.id);
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`Change the price of ${rule.charge_type.name}`}
        description="This version ends when the new one starts. Same scope, basis and priority."
      >
        <div className={styles.form}>
          <Input
            label={rule.basis === "percent" ? "New percent" : "New amount"}
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <MomentField label="From" value={from} onChange={setFrom} />
          <Textarea label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          {save.error ? (
            <Alert tone="danger" title="Not changed">
              {messageFor(errorOf(save.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!amountOk(amount, rule.basis)} loading={save.isPending} onClick={() => save.mutate()}>
              Change the price
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Stop the rule without a replacement: the next most specific rule applies after it (or none). */
function EndDialog({ rule, onClose }: { rule: Rule; onClose: () => void }) {
  const format = useCompanyFormat();
  const queryClient = useQueryClient();
  const [at, setAt] = useState<Date | null>(null);
  const end = useMutation({
    mutationFn: () => pricingActions.end(rule.id, { ends_at: momentOf(at, format.timezone) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["pricing"] });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`End ${rule.charge_type.name}?`}
        description="No new version starts. After it ends, a wider rule applies, or none: for a product price, that item can't be priced."
      >
        <div className={styles.form}>
          <MomentField label="Ends" value={at} onChange={setAt} />
          {end.error ? (
            <Alert tone="danger" title="Not ended">
              {messageFor(errorOf(end.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Keep it
            </Button>
            <Button variant="danger" loading={end.isPending} onClick={() => end.mutate()}>
              End
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** The signed contract behind a price; its link is fetched when opened (it expires in minutes). */
function ContractLink({ fileId }: { fileId: string }) {
  const open = useMutation({ mutationFn: () => openFile(fileId) });
  return (
    <span>
      <TextButton disabled={open.isPending} onClick={() => open.mutate()}>
        Open the contract
      </TextButton>
      {open.error ? <span className={styles.muted}> {messageFor(errorOf(open.error))}</span> : null}
    </span>
  );
}
